import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "../../../ui/Navbar";
import Modal from "../../../ui/Modal";
import { AuthContext } from "../../../../context/AuthContext";
import Table from "../../../ui/Table";
import Loader from "../../../ui/Loader";
import { swalConfirm, swalError, swalSuccess } from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import Select from "react-select";
import {
    DEFAULT_AI_MODELS,
    DEFAULT_AI_PROVIDERS,
    getGeminiModelConfig,
    getModelPreset,
} from "../../../../config/aiModelConfig";
import "../../../../App.css";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const EMPTY_FORM = {
    aiModelConfigName: "",
    inputCostPerMillionTokens: 0.0,
    outputCostPerMillionTokens: 0.0,
    thinkingCostPerMillionTokens: 0.0,
    temperature: "",
    topK: "",
    topP: "",
    frequencyPenalty: "",
    presencePenalty: "",
    mediaResolution: "",
};

const EMPTY_THINKING = {
    thinkingBudget: "",
    thinkingLevel: "",
    includeThoughts: false,
};

export default function ManageAIConfig() {
    const { isAuthenticated, primaryRole } = useContext(AuthContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
    const [viewingConfigRow, setViewingConfigRow] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");

    // LOV options
    const [aiProviders, setAiProviders] = useState(DEFAULT_AI_PROVIDERS);
    const [aiModels, setAIModels] = useState(DEFAULT_AI_MODELS);
    const [aiModelUseTypes, setAIModelUseTypes] = useState([]);

    // Shared form state (used for both create & edit)
    const [aiProvider, setAiProvider] = useState("gemini");
    const [aiModel, setAiModel] = useState(null);
    const [aiModelUseType, setAiModelUseType] = useState(null);
    const [isDefaultConfig, setIsDefaultConfig] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Track row id being set as default
    const [settingDefaultId, setSettingDefaultId] = useState(null);

    // Thinking config toggle + sub-fields
    const [thinkingEnabled, setThinkingEnabled] = useState(false);
    const [thinkingConfig, setThinkingConfig] = useState(EMPTY_THINKING);

    // Row being edited
    const [editingRow, setEditingRow] = useState(null);

    // Filter state (defaults to Gemini provider & Grader feature)
    const [selectedProviderFilter, setSelectedProviderFilter] = useState("gemini");
    const [selectedFeatureFilter, setSelectedFeatureFilter] = useState("Grader");

    // Overview stats
    const stats = useMemo(() => {
        const total = data.length;
        const geminiCount = data.filter((d) => (d.provider || "gemini").toLowerCase() === "gemini").length;
        const openAICount = data.filter((d) => (d.provider || "").toLowerCase() === "openai").length;
        const uniqueUseTypes = new Set(data.map((d) => d.type).filter(Boolean));
        const defaultCount = data.filter((d) => d.isDefaultConfig).length;
        return {
            total,
            geminiCount,
            openAICount,
            useTypeCount: uniqueUseTypes.size,
            defaultCount,
        };
    }, [data]);

    // Dynamic provider tabs (Gemini, OpenAI, etc.)
    const providerTabs = useMemo(() => {
        const counts = {};
        data.forEach((item) => {
            const p = (item.provider || "gemini").toLowerCase();
            counts[p] = (counts[p] || 0) + 1;
        });

        const tabs = [
            { id: "gemini", label: "Gemini", count: counts["gemini"] || 0, icon: "fa-solid fa-wand-magic-sparkles" },
            { id: "openai", label: "OpenAI", count: counts["openai"] || 0, icon: "fa-solid fa-brain" },
        ];

        Object.keys(counts).forEach((p) => {
            if (p !== "gemini" && p !== "openai") {
                tabs.push({
                    id: p,
                    label: p.charAt(0).toUpperCase() + p.slice(1),
                    count: counts[p],
                    icon: "fa-solid fa-microchip",
                });
            }
        });

        return tabs;
    }, [data]);

    // Data scoped to the currently selected provider
    const providerScopedData = useMemo(() => {
        return data.filter(
            (d) => (d.provider || "gemini").toLowerCase() === selectedProviderFilter.toLowerCase()
        );
    }, [data, selectedProviderFilter]);

    // Available feature types and their counts dynamically scoped to the selected provider
    const featureOptions = useMemo(() => {
        const counts = {};
        providerScopedData.forEach((d) => {
            if (d.type) {
                counts[d.type] = (counts[d.type] || 0) + 1;
            }
        });
        return Object.keys(counts)
            .sort()
            .map((feat) => ({
                value: feat,
                count: counts[feat],
            }));
    }, [providerScopedData]);

    // Switch provider tab and adjust feature filter if needed
    const handleProviderTabChange = (providerId) => {
        setSelectedProviderFilter(providerId);
        const scoped = data.filter(
            (d) => (d.provider || "gemini").toLowerCase() === providerId.toLowerCase()
        );
        const exists = scoped.some((d) => d.type === selectedFeatureFilter);
        if (!exists) {
            const hasGrader = scoped.some((d) => d.type === "Grader");
            if (hasGrader) {
                setSelectedFeatureFilter("Grader");
            } else if (scoped.length > 0 && scoped[0].type) {
                setSelectedFeatureFilter(scoped[0].type);
            }
        }
    };

    // Active default configuration for the selected feature and provider
    const currentDefaultConfig = useMemo(() => {
        return providerScopedData.find(
            (d) => d.type === selectedFeatureFilter && d.isDefaultConfig
        );
    }, [providerScopedData, selectedFeatureFilter]);

    // Filtered data for table view (both provider and feature)
    const filteredData = useMemo(() => {
        return [...providerScopedData]
            .filter((item) => {
                if (selectedFeatureFilter && item.type !== selectedFeatureFilter) {
                    return false;
                }
                return true;
            })
            .sort((a, b) => {
                const aDef = a.isDefaultConfig ? 1 : 0;
                const bDef = b.isDefaultConfig ? 1 : 0;
                if (bDef !== aDef) return bDef - aDef;

                const typeCompare = (a.type ?? "").localeCompare(b.type ?? "");
                if (typeCompare !== 0) return typeCompare;
                const provCompare = (a.provider || "gemini").localeCompare(b.provider || "gemini");
                if (provCompare !== 0) return provCompare;
                return (a.aiModelConfigName ?? "").localeCompare(b.aiModelConfigName ?? "");
            });
    }, [providerScopedData, selectedFeatureFilter]);

    // Dynamic table caption
    const tableCaption = useMemo(() => {
        let label = "AI Model Configurations";
        if (selectedProviderFilter === "gemini") label = "Google Gemini Configurations";
        else if (selectedProviderFilter === "openai") label = "OpenAI Configurations";
        else if (selectedProviderFilter) {
            label = `${selectedProviderFilter.charAt(0).toUpperCase() + selectedProviderFilter.slice(1)} Configurations`;
        }

        if (selectedFeatureFilter) {
            label += ` — ${selectedFeatureFilter}`;
        }
        return `${label} (${filteredData.length})`;
    }, [selectedProviderFilter, selectedFeatureFilter, filteredData.length]);

    const breadcrumbs = [
        { name: "Home", path: "/superadmin" },
        { name: "AI Config", path: "/superadmin/ai-config", isLast: true },
    ];

    // ── helpers ──────────────────────────────────────────────────────────────

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleProviderChange = (selectedOption) => {
        const newProvider = selectedOption?.value || "gemini";
        setAiProvider(newProvider);

        if (aiModel) {
            const isModelGemini = aiModel.toLowerCase().startsWith("gemini");
            const isModelOpenAI = aiModel.toLowerCase().includes("luna");
            if (
                (newProvider === "gemini" && isModelOpenAI) ||
                (newProvider === "openai" && isModelGemini)
            ) {
                setAiModel(newProvider === "openai" ? "gpt-6-luna" : null);
            } else {
                const preset = getModelPreset(aiModel, newProvider);
                if (preset) {
                    setFormData((prev) => ({
                        ...prev,
                        inputCostPerMillionTokens:
                            preset.inputCostPerMillionTokens !== undefined
                                ? preset.inputCostPerMillionTokens
                                : prev.inputCostPerMillionTokens,
                        outputCostPerMillionTokens:
                            preset.outputCostPerMillionTokens !== undefined
                                ? preset.outputCostPerMillionTokens
                                : prev.outputCostPerMillionTokens,
                        thinkingCostPerMillionTokens:
                            preset.thinkingCostPerMillionTokens !== undefined
                                ? preset.thinkingCostPerMillionTokens
                                : prev.thinkingCostPerMillionTokens,
                    }));
                }
            }
        }
    };

    // Filter available models based on selected provider
    const availableModelOptions = useMemo(() => {
        const currentProvider = (aiProvider || "gemini").toLowerCase();
        if (currentProvider === "openai") {
            // Only keep gpt luna for OpenAI
            const lunaModels = aiModels.filter((m) => m.value.toLowerCase().includes("luna"));
            return lunaModels.length > 0
                ? lunaModels
                : [{ value: "gpt-6-luna", label: "gpt-6-luna" }];
        }

        let list = aiModels.filter((m) => {
            const val = m.value.toLowerCase();
            return !val.startsWith("gpt") && !val.includes("luna");
        });

        // Ensure currently selected model is always present in options
        if (aiModel && !list.some((o) => o.value.toLowerCase() === aiModel.toLowerCase())) {
            list = [{ value: aiModel, label: aiModel }, ...list];
        }

        return list.length > 0 ? list : aiModels;
    }, [aiModels, aiProvider, aiModel]);

    const handleModelChange = (selectedOption) => {
        const modelValue = selectedOption?.value || null;
        setAiModel(modelValue);

        if (!modelValue) return;

        const preset = getModelPreset(modelValue, aiProvider) || getGeminiModelConfig(modelValue);
        if (preset) {
            setFormData((prev) => ({
                ...prev,
                inputCostPerMillionTokens:
                    preset.inputCostPerMillionTokens !== undefined
                        ? preset.inputCostPerMillionTokens
                        : prev.inputCostPerMillionTokens,
                outputCostPerMillionTokens:
                    preset.outputCostPerMillionTokens !== undefined
                        ? preset.outputCostPerMillionTokens
                        : prev.outputCostPerMillionTokens,
                thinkingCostPerMillionTokens:
                    preset.thinkingCostPerMillionTokens !== undefined
                        ? preset.thinkingCostPerMillionTokens
                        : prev.thinkingCostPerMillionTokens,
            }));
        }
    };

    const handleThinkingChange = (e) => {
        const { name, value, type, checked } = e.target;
        setThinkingConfig((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setAiProvider("gemini");
        setAiModel(null);
        setAiModelUseType(null);
        setIsDefaultConfig(false);
        setThinkingEnabled(false);
        setThinkingConfig(EMPTY_THINKING);
        setFormError("");
        setEditingRow(null);
    };

    const buildPayload = () => {
        let formattedThinkingConfig = null;
        if (thinkingEnabled) {
            formattedThinkingConfig = {
                ...thinkingConfig,
                thinkingBudget:
                    thinkingConfig.thinkingBudget !== "" &&
                    thinkingConfig.thinkingBudget !== null &&
                    thinkingConfig.thinkingBudget !== undefined
                        ? parseInt(thinkingConfig.thinkingBudget, 10)
                        : undefined,
                thinkingLevel: thinkingConfig.thinkingLevel || undefined,
            };
        }
        const payload = {
            aiModelConfigName: formData.aiModelConfigName,
            provider: (aiProvider || "gemini").toLowerCase(),
            model: aiModel || "",
            type: aiModelUseType || "",
            isDefaultConfig: isDefaultConfig,
            inputCostPerMillionTokens: formData.inputCostPerMillionTokens ?? 0,
            outputCostPerMillionTokens: formData.outputCostPerMillionTokens ?? 0,
            thinkingCostPerMillionTokens: formData.thinkingCostPerMillionTokens ?? 0,
            temperature: formData.temperature,
            topK: formData.topK,
            topP: formData.topP,
            frequencyPenalty: formData.frequencyPenalty,
            presencePenalty: formData.presencePenalty,
            mediaResolution: formData.mediaResolution || undefined,
            thinkingConfig: formattedThinkingConfig,
        };
        return payload;
    };

    // ── data fetching ─────────────────────────────────────────────────────────

    const fetchConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchWithAuth(
                `${serverUrl}/api/institute/getAllAIModelConfig`,
                { credentials: "include" },
            );
            if (!response) return;
            if (!response.ok) throw new Error("Failed to fetch AI model configurations");
            const result = await response.json();
            const configs = Array.isArray(result) ? result : result.data || [];
            const normalizedConfigs = configs.map((item) => ({
                ...item,
                provider: item.provider || "gemini",
            }));
            setData(normalizedConfigs);
        } catch (err) {
            setError(err.message);
            swalError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchLOVs = useCallback(async () => {
        try {
            const [res1, res2, res3] = await Promise.allSettled([
                fetchWithAuth(`${serverUrl}/api/lov/get-all-by-type/ai-model`),
                fetchWithAuth(`${serverUrl}/api/lov/get-all-by-type/ai-modelUseType`),
                fetchWithAuth(`${serverUrl}/api/lov/get-all-by-type/ai-provider`),
            ]);
            if (res1.status === "fulfilled" && res1.value?.ok) {
                const modelData = await res1.value.json();
                const fetchedModels = modelData.map((m) => ({ value: m.value, label: m.value }));
                setAIModels((prev) => {
                    const combined = [...prev];
                    fetchedModels.forEach((f) => {
                        if (!combined.some((m) => m.value.toLowerCase() === f.value.toLowerCase())) {
                            combined.push(f);
                        }
                    });
                    return combined;
                });
            }
            if (res2.status === "fulfilled" && res2.value?.ok) {
                const useTypeData = await res2.value.json();
                setAIModelUseTypes(useTypeData.map((t) => ({ value: t.value, label: t.value })));
            }
            if (res3.status === "fulfilled" && res3.value?.ok) {
                const providerData = await res3.value.json();
                if (Array.isArray(providerData) && providerData.length > 0) {
                    const fetchedProviders = providerData
                        .map((p) => ({
                            value: (p.value || p.name || "").toLowerCase(),
                            label: p.value || p.name || "",
                        }))
                        .filter((p) => p.value);
                    setAiProviders((prev) => {
                        const combined = [...prev];
                        fetchedProviders.forEach((f) => {
                            if (!combined.some((p) => p.value.toLowerCase() === f.value.toLowerCase())) {
                                combined.push(f);
                            }
                        });
                        return combined;
                    });
                }
            }
        } catch (err) {
            console.error("Failed to fetch LOVs:", err);
        }
    }, []);

    useEffect(() => {
        fetchConfigs();
        fetchLOVs();
    }, [fetchConfigs, fetchLOVs]);

    if (!isAuthenticated || primaryRole !== "superadmin") {
        return <Navigate to="/login" replace />;
    }

    // ── open edit modal ───────────────────────────────────────────────────────

    const openEditModal = (row) => {
        setEditingRow(row);
        setAiProvider(row.provider || "gemini");
        setAiModel(row.model || null);
        setAiModelUseType(row.type || null);
        setIsDefaultConfig(Boolean(row.isDefaultConfig));
        setFormData({
            aiModelConfigName: row.aiModelConfigName || "",
            inputCostPerMillionTokens: row.inputCostPerMillionTokens ?? 0,
            outputCostPerMillionTokens: row.outputCostPerMillionTokens ?? 0,
            thinkingCostPerMillionTokens: row.thinkingCostPerMillionTokens ?? 0,
            temperature: row.temperature || "",
            topK: row.topK || "",
            topP: row.topP || "",
            frequencyPenalty: row.frequencyPenalty || "",
            presencePenalty: row.presencePenalty || "",
            mediaResolution: row.mediaResolution || "",
        });
        const hasThinking = !!(
            row.thinkingConfig &&
            (row.thinkingConfig.thinkingLevel ||
                row.thinkingConfig.thinkingBudget !== undefined ||
                row.thinkingConfig.includeThoughts)
        );
        setThinkingEnabled(hasThinking);
        setThinkingConfig(
            hasThinking
                ? {
                    thinkingBudget:
                        row.thinkingConfig?.thinkingBudget !== undefined &&
                        row.thinkingConfig?.thinkingBudget !== null
                            ? row.thinkingConfig.thinkingBudget
                            : "",
                    thinkingLevel: row.thinkingConfig?.thinkingLevel || "",
                    includeThoughts: row.thinkingConfig?.includeThoughts || false,
                }
                : EMPTY_THINKING,
        );
        setFormError("");
        setIsEditModalOpen(true);
    };

    const openAdvanceModal = (row) => {
        setViewingConfigRow(row);
        setIsAdvanceModalOpen(true);
    };

    // ── set as default ────────────────────────────────────────────────────────

    const handleSetAsDefault = async (row) => {
        const configName = row.aiModelConfigName || row.model || "this configuration";
        const useType = row.type || "this feature";

        const result = await swalConfirm(
            "Set as Default Configuration",
            `Are you sure you want to set "${configName}" as the default AI configuration for "${useType}"? Any previous default for this feature will be replaced.`,
            "Yes, Set Default",
            "Cancel"
        );

        if (!result || !result.isConfirmed) return;

        setSettingDefaultId(row._id);
        try {
            const response = await fetchWithAuth(
                `${serverUrl}/api/institute/updateAIModelConfig/${row._id}`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        isDefaultConfig: true,
                        type: row.type,
                    }),
                }
            );

            if (!response) return;
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to set default AI model configuration");
            }

            swalSuccess(`"${configName}" is now the default configuration for "${useType}".`);
            fetchConfigs();
        } catch (err) {
            swalError(err.message);
        } finally {
            setSettingDefaultId(null);
        }
    };

    // ── create ────────────────────────────────────────────────────────────────

    const handleCreateAIModelConfig = async (e) => {
        e.preventDefault();
        setFormError("");
        
        // Validate thinking config
        if (
            thinkingEnabled &&
            !thinkingConfig.thinkingLevel &&
            (thinkingConfig.thinkingBudget === "" || thinkingConfig.thinkingBudget === undefined || thinkingConfig.thinkingBudget === null)
        ) {
            setFormError("Please enter a Thinking Budget (e.g. 2048) or select a Thinking Level when Thinking Config is enabled");
            setIsSubmitting(false);
            return;
        }

        if (thinkingEnabled && thinkingConfig.thinkingBudget !== "" && Number(thinkingConfig.thinkingBudget) < 0) {
            setFormError("Thinking Budget must be 0 or a positive integer");
            setIsSubmitting(false);
            return;
        }
        
        setIsSubmitting(true);
        try {
            const response = await fetchWithAuth(
                `${serverUrl}/api/institute/addAIModelConfig`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(buildPayload()),
                },
            );
            if (!response) return;
            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.errors && Array.isArray(errorData.errors)) {
                    swalError("Validation Error", errorData.errors.map((e) => `<p>${e}</p>`).join(""));
                    return;
                }
                throw new Error(errorData.message || "Failed to create AI Model Configuration");
            }
            setIsCreateModalOpen(false);
            resetForm();
            swalSuccess("Config created successfully");
            fetchConfigs();
        } catch (err) {
            setFormError(err.message);
            swalError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── update ────────────────────────────────────────────────────────────────

    const handleUpdateAIModelConfig = async (e) => {
        e.preventDefault();
        setFormError("");
        
        // Validate thinking config
        if (
            thinkingEnabled &&
            !thinkingConfig.thinkingLevel &&
            (thinkingConfig.thinkingBudget === "" || thinkingConfig.thinkingBudget === undefined || thinkingConfig.thinkingBudget === null)
        ) {
            setFormError("Please enter a Thinking Budget (e.g. 2048) or select a Thinking Level when Thinking Config is enabled");
            setIsSubmitting(false);
            return;
        }

        if (thinkingEnabled && thinkingConfig.thinkingBudget !== "" && Number(thinkingConfig.thinkingBudget) < 0) {
            setFormError("Thinking Budget must be 0 or a positive integer");
            setIsSubmitting(false);
            return;
        }
        
        setIsSubmitting(true);
        try {
            const response = await fetchWithAuth(
                `${serverUrl}/api/institute/updateAIModelConfig/${editingRow._id}`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(buildPayload()),
                },
            );
            if (!response) return;
            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.errors && Array.isArray(errorData.errors)) {
                    swalError("Validation Error", errorData.errors.map((e) => `<p>${e}</p>`).join(""));
                    return;
                }
                throw new Error(errorData.message || "Failed to update AI Model Configuration");
            }
            setIsEditModalOpen(false);
            resetForm();
            swalSuccess("Config updated successfully");
            fetchConfigs();
        } catch (err) {
            setFormError(err.message);
            swalError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── table columns ─────────────────────────────────────────────────────────

    const columns = [
        {
            key: "aiModelConfigName",
            label: "Config Name",
            sortable: true,
            render: (row) => (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span
                        style={{
                            fontWeight: row.aiModelConfigName ? 600 : 400,
                            color: row.aiModelConfigName ? "var(--text-color, #0f172a)" : "var(--text-muted, #94a3b8)",
                            fontStyle: row.aiModelConfigName ? "normal" : "italic",
                        }}
                    >
                        {row.aiModelConfigName || "Default"}
                    </span>
                    {row.isDefaultConfig && (
                        <span className="ai-default-badge" title="Active Default Configuration for this feature">
                            <i className="fa-solid fa-star" /> Default
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: "type",
            label: "Use Type",
            sortable: true,
            render: (row) => (
                <span
                    className="status-badge badge-requested"
                    style={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
                >
                    {row.type || "N/A"}
                </span>
            ),
        },
        {
            key: "provider",
            label: "Provider",
            sortable: true,
            sortBy: (row) => row.provider || "gemini",
            render: (row) => {
                const prov = (row.provider || "gemini").toLowerCase();
                const isGemini = prov === "gemini";
                const isOpenAI = prov === "openai";
                return (
                    <span
                        className={`status-badge ${
                            isGemini
                                ? "badge-provider-gemini"
                                : isOpenAI
                                ? "badge-provider-openai"
                                : "badge-provider-default"
                        }`}
                        style={{
                            textTransform: "capitalize",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                        }}
                    >
                        <i
                            className={
                                isGemini
                                    ? "fa-solid fa-wand-magic-sparkles"
                                    : isOpenAI
                                    ? "fa-solid fa-brain"
                                    : "fa-solid fa-microchip"
                            }
                            style={{ fontSize: "0.75rem" }}
                        />
                        {isGemini ? "Gemini" : isOpenAI ? "OpenAI" : row.provider || "Gemini"}
                    </span>
                );
            },
        },
        {
            key: "model",
            label: "Model",
            sortable: true,
            render: (row) => (
                <code className="ai-model-tag">
                    {row.model || "—"}
                </code>
            ),
        },
        {
            key: "inputCostPerMillionTokens",
            label: "Input Cost/M",
            sortable: true,
            render: (row) => (
                <span className="token-count" style={{ fontWeight: 600 }}>
                    {row.inputCostPerMillionTokens !== undefined && row.inputCostPerMillionTokens !== null
                        ? `$${row.inputCostPerMillionTokens}`
                        : "—"}
                </span>
            ),
        },
        {
            key: "outputCostPerMillionTokens",
            label: "Output Cost/M",
            sortable: true,
            render: (row) => (
                <span className="token-count" style={{ fontWeight: 600 }}>
                    {row.outputCostPerMillionTokens !== undefined && row.outputCostPerMillionTokens !== null
                        ? `$${row.outputCostPerMillionTokens}`
                        : "—"}
                </span>
            ),
        },
        {
            key: "thinkingCostPerMillionTokens",
            label: "Thinking Cost/M",
            sortable: true,
            render: (row) => (
                <span className="token-count" style={{ fontWeight: 600 }}>
                    {row.thinkingCostPerMillionTokens !== undefined && row.thinkingCostPerMillionTokens !== null
                        ? `$${row.thinkingCostPerMillionTokens}`
                        : "—"}
                </span>
            ),
        },
        {
            key: "advanceConfig",
            label: "Advance Config",
            sortable: false,
            render: (row) => {
                const hasThinking = Boolean(
                    row.thinkingConfig &&
                    (row.thinkingConfig.thinkingBudget !== undefined ||
                     row.thinkingConfig.thinkingLevel ||
                     row.thinkingConfig.includeThoughts)
                );
                const isDefault = Boolean(row.isDefaultConfig);
                const isSetting = settingDefaultId === row._id;
                return (
                    <div className="ai-advance-col-wrapper">
                        <button
                            className="view-btn advance-config-btn"
                            title="View Advance Configuration"
                            onClick={() => openAdvanceModal(row)}
                        >
                            <i className="fa-solid fa-sliders" />
                        </button>
                        {hasThinking && (
                            <span
                                className="thinking-indicator-chip"
                                title={`Thinking Config Active (${row.thinkingConfig?.thinkingBudget ? `${row.thinkingConfig.thinkingBudget}t` : row.thinkingConfig?.thinkingLevel || "On"})`}
                            >
                                🧠
                            </span>
                        )}
                        {isDefault ? (
                            <span
                                className="ai-default-pill-btn active"
                                title="Currently active default configuration for this feature"
                            >
                                <i className="fa-solid fa-star" />
                            </span>
                        ) : (
                            <button
                                className="ai-set-default-action-btn"
                                title="Set as default configuration for this feature"
                                onClick={() => handleSetAsDefault(row)}
                                disabled={isSetting}
                            >
                                {isSetting ? (
                                    <i className="fa-solid fa-circle-notch fa-spin" />
                                ) : (
                                    <i className="fa-regular fa-star" />
                                )}
                            </button>
                        )}
                    </div>
                );
            },
        },
        {
            key: "actions",
            label: "Actions",
            sortable: false,
            render: (row) => (
                <button
                    className="edit-btn"
                    title="Edit"
                    onClick={() => openEditModal(row)}
                >
                    <i className="fa-solid fa-pen-to-square" /> Edit
                </button>
            ),
        },
    ];

    // ── shared form body ──────────────────────────────────────────────────────

    const renderFormFields = () => (
        <>
            {/* Config Name */}
            <div className="form-group">
                <label htmlFor="aiModelConfigName">
                    <i className="fa-solid fa-asterisk requiredIcon" /> Config Name
                </label>
                <input
                    type="text"
                    id="aiModelConfigName"
                    name="aiModelConfigName"
                    value={formData.aiModelConfigName}
                    onChange={handleInputChange}
                    placeholder="e.g. Gemini-Grader-Config"
                    required
                />
            </div>

            {/* AI Provider */}
            <div className="form-group">
                <label>
                    <i className="fa-solid fa-asterisk requiredIcon" /> Provider
                </label>
                <Select
                    id="aiProvider"
                    name="AI Provider"
                    options={aiProviders}
                    value={
                        aiProviders.find(
                            (o) => o.value.toLowerCase() === (aiProvider || "gemini").toLowerCase()
                        ) || { value: aiProvider, label: aiProvider }
                    }
                    onChange={handleProviderChange}
                    placeholder="Select Provider..."
                    isClearable={false}
                    required
                />
            </div>

            {/* AI Model Use Type */}
            <div className="form-group">
                <label>
                    <i className="fa-solid fa-asterisk requiredIcon" /> AI Model Use
                </label>
                <Select
                    id="modelUseType"
                    name="AI Model Use"
                    options={aiModelUseTypes}
                    value={aiModelUseTypes.find((o) => o.value === aiModelUseType) || null}
                    onChange={(sel) => setAiModelUseType(sel?.value)}
                    placeholder="Select AI Model Use ..."
                    isClearable
                    required
                />
            </div>

            {/* AI Model */}
            <div className="form-group">
                <label>
                    <i className="fa-solid fa-asterisk requiredIcon" /> AI Model
                </label>
                <Select
                    id="model"
                    name="AIModel"
                    options={availableModelOptions}
                    value={
                        availableModelOptions.find((o) => o.value === aiModel) ||
                        (aiModel ? { value: aiModel, label: aiModel } : null)
                    }
                    onChange={handleModelChange}
                    placeholder="Select AI Model..."
                    isClearable
                    required
                />
            </div>

            {/* ── Set as Default Configuration Toggle ── */}
            <div className="form-group default-toggle-group">
                <div className="default-toggle-header">
                    <label className="default-toggle-label">
                        Set as Default
                    </label>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={isDefaultConfig}
                            onChange={(e) => setIsDefaultConfig(e.target.checked)}
                        />
                        <span className="toggle-slider" />
                    </label>
                </div>
            </div>

            {/* Cost fields */}
            <div className="form-group">
                <label htmlFor="inputCostPerMillionTokens">Input Cost / M Tokens</label>
                <input
                    type="text"
                    id="inputCostPerMillionTokens"
                    name="inputCostPerMillionTokens"
                    value={formData.inputCostPerMillionTokens}
                    onChange={handleInputChange}
                    placeholder="e.g. 0.1"
                />
            </div>
            <div className="form-group">
                <label htmlFor="outputCostPerMillionTokens">Output Cost / M Tokens</label>
                <input
                    type="text"
                    id="outputCostPerMillionTokens"
                    name="outputCostPerMillionTokens"
                    value={formData.outputCostPerMillionTokens}
                    onChange={handleInputChange}
                    placeholder="e.g. 0.1"
                />
            </div>
            <div className="form-group">
                <label htmlFor="thinkingCostPerMillionTokens">Thinking Cost / M Tokens</label>
                <input
                    type="text"
                    id="thinkingCostPerMillionTokens"
                    name="thinkingCostPerMillionTokens"
                    value={formData.thinkingCostPerMillionTokens}
                    onChange={handleInputChange}
                    placeholder="e.g. 0.1"
                />
            </div>

            {/* Sampling params */}
            <div className="form-group">
                <label htmlFor="temperature">Temperature</label>
                <input
                    type="number"
                    id="temperature"
                    name="temperature"
                    value={formData.temperature}
                    onChange={handleInputChange}
                    min="0.0"
                    max="2.0"
                    step="0.1"
                    placeholder="Range: 0.0 to 2.0 (e.g. 0.7)"
                />
            </div>
            <div className="form-group">
                <label htmlFor="topK">Top K</label>
                <input
                    type="number"
                    id="topK"
                    name="topK"
                    value={formData.topK}
                    onChange={handleInputChange}
                    min="1"
                    step="1"
                    placeholder="Range: 1 and above (e.g. 40)"
                />
            </div>
            <div className="form-group">
                <label htmlFor="topP">Top P</label>
                <input
                    type="number"
                    id="topP"
                    name="topP"
                    value={formData.topP}
                    onChange={handleInputChange}
                    min="0.0"
                    max="1.0"
                    step="0.1"
                    placeholder="Range: 0.0 to 1.0 (e.g. 0.9)"
                />
            </div>
            <div className="form-group">
                <label htmlFor="frequencyPenalty">Frequency Penalty</label>
                <input
                    type="number"
                    id="frequencyPenalty"
                    name="frequencyPenalty"
                    value={formData.frequencyPenalty}
                    onChange={handleInputChange}
                    min="-2.0"
                    max="2.0"
                    step="0.1"
                    placeholder="Range: -2.0 to 2.0 (e.g. 0)"
                />
            </div>
            <div className="form-group">
                <label htmlFor="presencePenalty">Presence Penalty</label>
                <input
                    type="number"
                    id="presencePenalty"
                    name="presencePenalty"
                    value={formData.presencePenalty}
                    onChange={handleInputChange}
                    min="-2.0"
                    max="2.0"
                    step="0.1"
                    placeholder="Range: -2.0 to 2.0 (e.g. 0)"
                />
            </div>

            {/* Media Resolution */}
            <div className="form-group">
                <label htmlFor="mediaResolution">Media Resolution</label>
                <select
                    id="mediaResolution"
                    name="mediaResolution"
                    value={formData.mediaResolution}
                    onChange={handleInputChange}
                >
                    <option value="">Default (Unset / Auto)</option>
                    <option value="MEDIA_RESOLUTION_LOW">Low (512x512, ~66-258 tokens)</option>
                    <option value="MEDIA_RESOLUTION_MEDIUM">Medium (1024x1024, ~532 tokens)</option>
                    <option value="MEDIA_RESOLUTION_HIGH">High (Full detail, 1000+ tokens)</option>
                </select>
                <small style={{ color: "var(--text-muted, #888)", display: "block", marginTop: "4px" }}>
                    Leave unset for Gemini default. Set "Medium" to prevent token spikes on camera uploads.
                </small>
            </div>

            {/* ── Thinking Config toggle ──────────────────────────────── */}
            <div className="form-group thinking-toggle-group">
                <div className="thinking-toggle-header">
                    <label className="thinking-toggle-label">
                        Thinking Config
                    </label>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={thinkingEnabled}
                            onChange={(e) => {
                                setThinkingEnabled(e.target.checked);
                                if (!e.target.checked) setThinkingConfig(EMPTY_THINKING);
                            }}
                        />
                        <span className="toggle-slider" />
                    </label>
                </div>

                {thinkingEnabled && (
                    <div className="thinking-sub-fields">
                        <div className="form-group">
                            <label htmlFor="thinkingBudget">
                                Thinking Budget (Tokens)
                            </label>
                            <input
                                type="number"
                                id="thinkingBudget"
                                name="thinkingBudget"
                                value={thinkingConfig.thinkingBudget}
                                onChange={handleThinkingChange}
                                min="0"
                                max="65536"
                                step="1"
                                placeholder="e.g. 2048 (0 to disable thinking)"
                            />
                            <small style={{ color: "var(--text-muted, #888)", display: "block", marginTop: "4px" }}>
                                Recommended: 2048 tokens. Limits thinking in batch mode while maintaining grading accuracy. Set 0 to disable.
                            </small>
                        </div>
                        <div className="form-group">
                            <label htmlFor="thinkingLevel">
                                Thinking Level
                            </label>
                            <select
                                id="thinkingLevel"
                                name="thinkingLevel"
                                value={thinkingConfig.thinkingLevel}
                                onChange={handleThinkingChange}
                            >
                                <option value="">Select Thinking Level (optional)...</option>
                                <option value="MINIMAL">MINIMAL</option>
                                <option value="LOW">LOW</option>
                                <option value="MEDIUM">MEDIUM</option>
                                <option value="HIGH">HIGH</option>
                            </select>
                        </div>
                        <div className="form-group include-thoughts-toggle-group">
                            <div className="include-thoughts-toggle-header">
                                <label className="include-thoughts-label">Include Thoughts in Response</label>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        name="includeThoughts"
                                        checked={thinkingConfig.includeThoughts}
                                        onChange={handleThinkingChange}
                                    />
                                    <span className="toggle-slider" />
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <>
            <Navbar
                createButtons={[
                    {
                        text: "Create AI Config",
                        onClick: () => {
                            resetForm();
                            if (selectedProviderFilter !== "all") {
                                setAiProvider(selectedProviderFilter);
                            }
                            if (selectedFeatureFilter !== "all") {
                                setAiModelUseType(selectedFeatureFilter);
                            }
                            setIsCreateModalOpen(true);
                        },
                    },
                ]}
                pageName="AI Config"
                activePage="AI Config"
                breadcrumbs={breadcrumbs}
            />

            <div className="manage-token ai-config-page-wrapper">
                {/* ── Top Summary / KPI Cards ────────────────────────────────────── */}
                <div className="ai-stats-row">
                    <div
                        className="ai-stat-card neutral-card"
                        title="Total AI configurations across all providers"
                    >
                        <div className="ai-stat-icon total">
                            <i className="fa-solid fa-layer-group" />
                        </div>
                        <div className="ai-stat-content">
                            <div className="ai-stat-number">{stats.total}</div>
                            <div className="ai-stat-title">Total Configurations</div>
                        </div>
                    </div>

                    <div
                        className={`ai-stat-card gemini-card ${selectedProviderFilter === "gemini" ? "active" : ""}`}
                        onClick={() => handleProviderTabChange("gemini")}
                        title="Click to filter by Gemini configurations"
                        role="button"
                        tabIndex={0}
                    >
                        <div className="ai-stat-icon gemini">
                            <i className="fa-solid fa-wand-magic-sparkles" />
                        </div>
                        <div className="ai-stat-content">
                            <div className="ai-stat-number">{stats.geminiCount}</div>
                            <div className="ai-stat-title">Google Gemini Models</div>
                        </div>
                    </div>

                    <div
                        className={`ai-stat-card openai-card ${selectedProviderFilter === "openai" ? "active" : ""}`}
                        onClick={() => handleProviderTabChange("openai")}
                        title="Click to filter by OpenAI configurations"
                        role="button"
                        tabIndex={0}
                    >
                        <div className="ai-stat-icon openai">
                            <i className="fa-solid fa-brain" />
                        </div>
                        <div className="ai-stat-content">
                            <div className="ai-stat-number">{stats.openAICount}</div>
                            <div className="ai-stat-title">OpenAI Models</div>
                        </div>
                    </div>

                    <div className="ai-stat-card neutral-card">
                        <div className="ai-stat-icon features">
                            <i className="fa-solid fa-shapes" />
                        </div>
                        <div className="ai-stat-content">
                            <div className="ai-stat-number">{stats.useTypeCount}</div>
                            <div className="ai-stat-title">Configured AI Features</div>
                        </div>
                    </div>

                    <div className="ai-stat-card default-card neutral-card" title="Total active default configurations across use types">
                        <div className="ai-stat-icon defaults">
                            <i className="fa-solid fa-star" />
                        </div>
                        <div className="ai-stat-content">
                            <div className="ai-stat-number">{stats.defaultCount}</div>
                            <div className="ai-stat-title">Active Defaults</div>
                        </div>
                    </div>
                </div>

                {/* ── Navigation Section: Providers & Features ───────────────────── */}
                <div className="ai-nav-section">
                    {/* Provider Nav Buttons */}
                    <div className="ai-nav-group">
                        <div className="ai-provider-tabs">
                            {providerTabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={`ai-provider-tab-btn ${selectedProviderFilter === tab.id ? "active" : ""} tab-${tab.id}`}
                                    onClick={() => handleProviderTabChange(tab.id)}
                                >
                                    <i className={tab.icon} />
                                    <span className="tab-label">{tab.label}</span>
                                    <span className="ai-tab-count-badge">{tab.count}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Feature Nav Buttons */}
                    <div className="ai-nav-group">
                        <div className="ai-feature-tabs">
                            {featureOptions.map(({ value, count }) => {
                                const hasDefault = providerScopedData.some((d) => d.type === value && d.isDefaultConfig);
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        className={`ai-feature-tab-btn ${selectedFeatureFilter === value ? "active" : ""}`}
                                        onClick={() => setSelectedFeatureFilter(value)}
                                    >
                                        {hasDefault && (
                                            <i
                                                className="fa-solid fa-star feature-has-default-star"
                                                title="Has active default configuration"
                                            />
                                        )}
                                        <span>{value}</span>
                                        <span className="ai-tab-count-badge">{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ── Active Default Configuration Banner ────────────────────────── */}
                {currentDefaultConfig ? (
                    <div className="ai-current-default-card">
                        <div className="ai-default-card-left">
                            <div className="ai-default-card-icon">
                                <i className="fa-solid fa-star" />
                            </div>
                            <div className="ai-default-card-info">
                                <div className="ai-default-card-label">
                                    Active Default for <span className="highlight-feature">{selectedFeatureFilter}</span>
                                </div>
                                <div className="ai-default-card-name">
                                    <span className="name">{currentDefaultConfig.aiModelConfigName || "Unnamed Config"}</span>
                                    <span className="ai-default-dot">•</span>
                                    <code className="ai-model-tag">{currentDefaultConfig.model || "—"}</code>
                                    <span className="ai-default-cost">
                                        (${currentDefaultConfig.inputCostPerMillionTokens ?? 0} in / ${currentDefaultConfig.outputCostPerMillionTokens ?? 0} out per 1M)
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="ai-default-card-right">
                            <button
                                type="button"
                                className="ai-default-quick-edit-btn"
                                onClick={() => openEditModal(currentDefaultConfig)}
                                title="Edit default configuration"
                            >
                                <i className="fa-solid fa-pen-to-square" /> Edit Default
                            </button>
                        </div>
                    </div>
                ) : filteredData.length > 0 && selectedFeatureFilter ? (
                    <div className="ai-no-default-notice">
                        <i className="fa-solid fa-circle-info" />
                        <span>
                            No default configuration is currently set for <strong>{selectedFeatureFilter}</strong>.
                            Click <strong>"Set Default"</strong> on any configuration below to make it the standard model.
                        </span>
                    </div>
                ) : null}

                {/* ── Main Content Area ─────────────────────────────────────────── */}
                <div className="table-section">
                    {loading ? (
                        <Loader style={{ fontSize: "2.2rem" }} />
                    ) : error ? (
                        <div className="error-container">
                            <div className="error-icon">⚠️</div>
                            <h2>Error Loading Data</h2>
                            <p>{error}</p>
                            <button onClick={fetchConfigs} className="retry-btn">
                                <span>🔄</span> Retry
                            </button>
                        </div>
                    ) : (
                        <Table
                            data={filteredData}
                            columns={columns}
                            caption={tableCaption}
                            leftSearchActions={
                                (selectedProviderFilter !== "gemini" || selectedFeatureFilter !== "Grader") ? (
                                    <button
                                        type="button"
                                        className="ai-reset-filters-btn"
                                        onClick={() => {
                                            setSelectedProviderFilter("gemini");
                                            setSelectedFeatureFilter("Grader");
                                        }}
                                        title="Reset to default (Gemini & Grader)"
                                    >
                                        <i className="fa-solid fa-rotate-left" /> Reset to Default
                                    </button>
                                ) : null
                            }
                        />
                    )}
                </div>
            </div>

            {/* ── Create Modal ────────────────────────────────────────────── */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={() => !isSubmitting && (setIsCreateModalOpen(false), resetForm())}
            >
                <h2>Create New AI Model Config</h2>
                <form onSubmit={handleCreateAIModelConfig}>
                    {renderFormFields()}
                    {formError && <div className="error-message">{formError}</div>}
                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <Loader style={{ fontSize: "1em", color: "white", top: "0", left: "0" }} />
                        ) : (
                            <>
                                <i className="fa-solid fa-plus" /> Create AI Model Configuration
                            </>
                        )}
                    </button>
                </form>
            </Modal>

            {/* ── Edit Modal ──────────────────────────────────────────────── */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => !isSubmitting && (setIsEditModalOpen(false), resetForm())}
            >
                <h2>Edit AI Model Config</h2>
                <form onSubmit={handleUpdateAIModelConfig}>
                    {renderFormFields()}
                    {formError && <div className="error-message">{formError}</div>}
                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <Loader style={{ fontSize: "1em", color: "white", top: "0", left: "0" }} />
                        ) : (
                            <>
                                <i className="fa-solid fa-floppy-disk" /> Update AI Model Configuration
                            </>
                        )}
                    </button>
                </form>
            </Modal>

            {/* ── Advance Config Modal ────────────────────────────────────── */}
            <Modal
                isOpen={isAdvanceModalOpen}
                onClose={() => setIsAdvanceModalOpen(false)}
                maxWidth="620px"
            >
                <div className="advance-modal-header">
                    <h2>
                        <i className="fa-solid fa-sliders" style={{ marginRight: "10px", color: "var(--color-3, #4f46e5)" }} />
                        Advance Configuration
                    </h2>
                    {viewingConfigRow && (
                        <div className="advance-modal-subtitle">
                            <span className="badge-config-name">{viewingConfigRow.aiModelConfigName || "Unnamed Config"}</span>
                            <span className="badge-dot">•</span>
                            <span
                                className={`status-badge ${
                                    (viewingConfigRow.provider || "gemini").toLowerCase() === "openai"
                                        ? "badge-provider-openai"
                                        : "badge-provider-gemini"
                                }`}
                                style={{
                                    fontSize: "0.75rem",
                                    padding: "0.15rem 0.55rem",
                                    textTransform: "capitalize",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }}
                            >
                                <i
                                    className={
                                        (viewingConfigRow.provider || "gemini").toLowerCase() === "openai"
                                            ? "fa-solid fa-brain"
                                            : "fa-solid fa-wand-magic-sparkles"
                                    }
                                    style={{ fontSize: "0.7rem" }}
                                />
                                {viewingConfigRow.provider || "gemini"}
                            </span>
                            <span className="badge-dot">•</span>
                            <span className="badge-model-name">{viewingConfigRow.model || "—"}</span>
                            {viewingConfigRow.type && (
                                <>
                                    <span className="badge-dot">•</span>
                                    <span
                                        className="status-badge badge-requested"
                                        style={{ textTransform: "none", fontSize: "0.75rem", padding: "0.15rem 0.55rem" }}
                                    >
                                        {viewingConfigRow.type}
                                    </span>
                                </>
                            )}
                            {viewingConfigRow.isDefaultConfig && (
                                <>
                                    <span className="badge-dot">•</span>
                                    <span className="ai-default-badge">
                                        <i className="fa-solid fa-star" /> Default Config
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {viewingConfigRow && (
                    <div className="advance-config-content">
                        {/* Section 1: Sampling & Generation */}
                        <div className="config-section">
                            <h3 className="config-section-title">
                                <i className="fa-solid fa-wave-square" /> Sampling & Model Parameters
                            </h3>
                            <div className="config-grid">
                                <div className="config-card">
                                    <span className="config-label">Default Config</span>
                                    <span className="config-val">
                                        {viewingConfigRow.isDefaultConfig ? (
                                            <span style={{ color: "#b45309", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                                <i className="fa-solid fa-star" style={{ color: "#f59e0b" }} /> Yes (Default)
                                            </span>
                                        ) : (
                                            <span style={{ color: "var(--text-muted, #64748b)" }}>No</span>
                                        )}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Provider</span>
                                    <span className="config-val" style={{ textTransform: "capitalize" }}>
                                        {viewingConfigRow.provider || "gemini"}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Temperature</span>
                                    <span className="config-val">
                                        {viewingConfigRow.temperature !== undefined && viewingConfigRow.temperature !== null && viewingConfigRow.temperature !== ""
                                            ? viewingConfigRow.temperature
                                            : "-"}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Top K</span>
                                    <span className="config-val">
                                        {viewingConfigRow.topK !== undefined && viewingConfigRow.topK !== null && viewingConfigRow.topK !== ""
                                            ? viewingConfigRow.topK
                                            : "-"}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Top P</span>
                                    <span className="config-val">
                                        {viewingConfigRow.topP !== undefined && viewingConfigRow.topP !== null && viewingConfigRow.topP !== ""
                                            ? viewingConfigRow.topP
                                            : "-"}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Frequency Penalty</span>
                                    <span className="config-val">
                                        {viewingConfigRow.frequencyPenalty !== undefined && viewingConfigRow.frequencyPenalty !== null && viewingConfigRow.frequencyPenalty !== ""
                                            ? viewingConfigRow.frequencyPenalty
                                            : "-"}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Presence Penalty</span>
                                    <span className="config-val">
                                        {viewingConfigRow.presencePenalty !== undefined && viewingConfigRow.presencePenalty !== null && viewingConfigRow.presencePenalty !== ""
                                            ? viewingConfigRow.presencePenalty
                                            : "-"}
                                    </span>
                                </div>
                                <div className="config-card">
                                    <span className="config-label">Media Resolution</span>
                                    <span className="config-val">
                                        {viewingConfigRow.mediaResolution === "MEDIA_RESOLUTION_LOW"
                                            ? "Low (512x512)"
                                            : viewingConfigRow.mediaResolution === "MEDIA_RESOLUTION_MEDIUM"
                                            ? "Medium (1024x1024)"
                                            : viewingConfigRow.mediaResolution === "MEDIA_RESOLUTION_HIGH"
                                            ? "High (Full detail)"
                                            : viewingConfigRow.mediaResolution || "Default (Auto)"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Thinking Config */}
                        <div className="config-section" style={{ marginTop: "18px" }}>
                            <div className="config-section-header">
                                <h3 className="config-section-title" style={{ margin: 0 }}>
                                    <i className="fa-solid fa-brain" /> Thinking Config
                                </h3>
                                {viewingConfigRow.thinkingConfig &&
                                (viewingConfigRow.thinkingConfig.thinkingLevel ||
                                    viewingConfigRow.thinkingConfig.thinkingBudget !== undefined ||
                                    viewingConfigRow.thinkingConfig.includeThoughts) ? (
                                    <span className="status-badge badge-active" style={{ fontSize: "0.75rem" }}>Enabled</span>
                                ) : (
                                    <span className="status-badge badge-default" style={{ fontSize: "0.75rem" }}>Disabled</span>
                                )}
                            </div>

                            {viewingConfigRow.thinkingConfig &&
                            (viewingConfigRow.thinkingConfig.thinkingLevel ||
                                viewingConfigRow.thinkingConfig.thinkingBudget !== undefined ||
                                viewingConfigRow.thinkingConfig.includeThoughts) ? (
                                <div className="config-grid" style={{ marginTop: "12px" }}>
                                    <div className="config-card">
                                        <span className="config-label">Thinking Budget</span>
                                        <span className="config-val">
                                            {viewingConfigRow.thinkingConfig.thinkingBudget !== undefined && viewingConfigRow.thinkingConfig.thinkingBudget !== null
                                                ? `${viewingConfigRow.thinkingConfig.thinkingBudget} tokens`
                                                : "Unset"}
                                        </span>
                                    </div>
                                    <div className="config-card">
                                        <span className="config-label">Thinking Level</span>
                                        <span className="config-val">
                                            {viewingConfigRow.thinkingConfig.thinkingLevel || "Unset"}
                                        </span>
                                    </div>
                                    <div className="config-card">
                                        <span className="config-label">Include Thoughts</span>
                                        <span className="config-val">
                                            {viewingConfigRow.thinkingConfig.includeThoughts ? "Yes" : "No"}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <p style={{ color: "var(--text-muted, #888)", fontSize: "0.85rem", marginTop: "10px", fontStyle: "italic" }}>
                                    No custom thinking configuration set. Default thinking behavior for the selected model will apply.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

        </>
    );
}
